import {
  buttonVariants,
  DimeCard,
  DimeCardAction,
  DimeCardDescription,
  DimeCardHeader,
  DimeCardTitle,
  DimeDataTable,
} from '@nudool/design-system';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Building2 } from 'lucide-react';
import { useMemo } from 'react';
import { ListEmptyState } from '../../components/ListEmptyState.js';
import { ListErrorState } from '../../components/ListErrorState.js';
import {
  parseListRouteSearch,
  type ListRouteSearch,
} from '../../hooks/use-list-table-helpers/index.js';
import { useListTable } from '../../hooks/use-list-table.js';
import { PermissionGuard } from '../../lib/permission-guard.js';
import { makeDepartmentColumns } from '../../modules/departments/department-columns.js';
import {
  GET_DEPARTMENTS_LIST,
  type Department,
  type DepartmentWhere,
  type GetDepartmentsListQueryResult,
  type GetDepartmentsListQueryVariables,
} from '../../modules/departments/operations.js';
import { usePermissions } from '../../modules/iam/permissions-context.js';

// Column id -> Prisma orderBy path, verified against SORTABLE_FIELDS.Department.
export const DEPARTMENT_SORT_MAP: Record<string, string[]> = {
  name: ['name'],
  activeFlag: ['activeFlag'],
};

const DEPARTMENTS_WHERE: DepartmentWhere = { activeFlag: { equals: true } };

export const Route = createFileRoute('/departments/')({
  validateSearch: (search: Record<string, unknown>): ListRouteSearch =>
    parseListRouteSearch(search),
  component: SetupDepartmentsPage,
});

// eslint-disable-next-line react-refresh/only-export-components
function SetupDepartmentsPage() {
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const canEdit = permissions.includes('department:edit');
  const columns = useMemo(() => makeDepartmentColumns(navigate, canEdit), [navigate, canEdit]);

  const {
    rows: departments,
    loading,
    error,
    refetch,
    tableProps,
    search,
  } = useListTable<
    GetDepartmentsListQueryResult,
    GetDepartmentsListQueryVariables,
    Department,
    DepartmentWhere
  >({
    query: GET_DEPARTMENTS_LIST,
    extractConnection: (data) => data?.departments,
    defaultSort: [{ id: 'name', desc: false }],
    sortMap: DEPARTMENT_SORT_MAP,
    sortTiebreakerField: null, // DepartmentOrderBy has no `id` field yet.
    where: DEPARTMENTS_WHERE,
  });

  return (
    <DimeCard borderless className="gap-0 py-0">
      <DimeCardHeader className="px-0 mb-4">
        <DimeCardTitle className="text-base flex items-center gap-2">Departments</DimeCardTitle>
        <DimeCardDescription className="text-sm">
          Define departments used to organize roles and staff.
        </DimeCardDescription>
        <DimeCardAction>
          <PermissionGuard
            required={['department:edit']}
            pass={
              <Link to="/departments/new" className={buttonVariants({})}>
                + New Department
              </Link>
            }
            fail={
              <span className={buttonVariants({})} role="link" aria-disabled="true">
                + New Department
              </span>
            }
          />
        </DimeCardAction>
      </DimeCardHeader>

      {error && departments.length === 0 ? (
        <ListErrorState
          title="Could not load departments"
          message={error.message}
          onRetry={() => void refetch()}
        />
      ) : !loading && departments.length === 0 && !search ? (
        <ListEmptyState
          icon={<Building2 />}
          title="No departments yet"
          description="Get started by creating your first department."
          action={
            <PermissionGuard
              required={['department:edit']}
              pass={
                <Link to="/departments/new" className={buttonVariants({ size: 'sm' })}>
                  + New Department
                </Link>
              }
              fail={
                <span className={buttonVariants({ size: 'sm' })} role="link" aria-disabled="true">
                  + New Department
                </span>
              }
            />
          }
        />
      ) : (
        <DimeDataTable
          columns={columns}
          data={departments}
          maxBodyHeight="viewport"
          searchColumn="name"
          searchPlaceholder="Search by name…"
          onRowClick={(department) =>
            navigate({
              to: '/departments/$departmentId',
              params: { departmentId: department.id },
              search: { edit: false },
            })
          }
          showViewOptions
          emptyMessage="No departments found. Try adjusting your search."
          loadingRowCount={tableProps.pageSize}
          exportFileName="departments"
          {...tableProps}
        />
      )}
    </DimeCard>
  );
}
